import {
  getBlockedDropTargetIds,
  getGoalFolderChildren,
  onGoalReorder,
} from '@griply/utils';
import { useResolveGoalParentChanges } from 'features/goals';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  TreeItemMove,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useGoalTree } from '../../hooks/use-goal-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './goal-grouping-tasks-list.style';
import { GoalRenderData, GoalRenderDataContext } from './goal-render-context';
import { GoalRenderItem } from './goal-render-item';

const goalDragTypes = [TaskListItemTypes.Task, TaskListItemTypes.Goal];
const taskItemTypes = [TaskListItemTypes.Task];

// A goal leaves a parent by dropping on another folder, never on the root.
const dropTypes = {
  [TaskListItemTypes.Root]: [],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Task, TaskListItemTypes.Goal],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
  [TaskListItemTypes.GoalDeadline]: [],
};

const goalGroupingRootId = 'goal-grouping-root';

export const GoalGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
  canReorderGoals,
}) => {
  const today = useToday();
  const links = useEntityLinks();
  const resolveGoalParentChanges = useResolveGoalParentChanges();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  // Set when the list is one goal's own subtree: that goal is the fixed root folder.
  const rootGoalId = groupSettings.goalId;

  const { flattenSubGoals } = groupSettings;
  const goalChildrenByParentId = useMemo(
    () =>
      flattenSubGoals
        ? undefined
        : getGoalFolderChildren(goals, { links, rootGoalId }),
    [flattenSubGoals, goals, links, rootGoalId],
  );

  const treeItems = useGoalTree({
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNoGoalGroup: groupSettings.hideNoGoalGroup,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
    rootId: goalGroupingRootId,
    goalChildrenByParentId,
  });

  const isItemDraggable = useCallback(
    (item: TreeItem) => item.id !== rootGoalId,
    [rootGoalId],
  );

  const canDropOnItem = useCallback(
    (target: TreeItem, draggedItems: TreeItem[]) =>
      draggedItems.every(
        (dragged) =>
          dragged.type !== TaskListItemTypes.Goal ||
          !getBlockedDropTargetIds(dragged.id, goals, links).has(target.id),
      ),
    [goals, links],
  );

  const _onReorder = (updatedItems: TreeItem[], moves: TreeItemMove[]) => {
    if (!onReorder) {
      return;
    }

    const result = onGoalReorder({
      updatedItems,
      oldItems: Object.values(treeItems),
      tasksMap,
      rootSectionId: rootId,
    });

    onReorder(
      result.updatedSections,
      result.updatedTasks,
      result.relationshipUpdates,
      canReorderGoals
        ? resolveGoalParentChanges(moves, [goalGroupingRootId])
        : [],
    );
  };

  const renderData = useMemo<GoalRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      goals,
      lifeAreas,
      links,
      today,
      denseFormat,
      defaultNewTaskDate,
    }),
    [
      groupSettings,
      tasksMap,
      tasks,
      goals,
      lifeAreas,
      links,
      today,
      denseFormat,
      defaultNewTaskDate,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <GoalRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={goalGroupingRootId}
          items={treeItems}
          renderItem={GoalRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={canReorderGoals ? goalDragTypes : taskItemTypes}
          isItemDraggable={isItemDraggable}
          canDropOnItem={canDropOnItem}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
          dropTypes={dropTypes}
        />
      </GoalRenderDataContext.Provider>
    </Styled.Container>
  );
};
